<template>
    <div class="form-editor">
        <transition
            name="fade"
            mode="out-in"
        >
            <div
                v-if="withScrollTop && showScrollTop"
                class="form-editor-scrolltop"
                @click="onScrollTopClick"
            >
                <i class="form-editor-scrolltop-icon fas fa-arrow-alt-up" />
            </div>
        </transition>
        <div class="form-editor-container" />
    </div>
</template>
<script>
import { throttle } from 'lodash';
import Quill from 'quill';

export default {
    model: {
        event: 'change',
        prop: 'value',
    },

    props: {
        value: {
            type: String,
            default: '',
        },
        withScrollTop: {
            type: Boolean,
            default: true,
        },
    },

    data: () => ({
        container: null,
        editor: null,
        editorContent: null,
        showScrollTop: false,
    }),

    /**
     * Mounts component.
     */
    async mounted() {
        this.container = this.$el.querySelector('.form-editor-container');

        const { editor, editorContent } = await this.initializeEditor();
        this.editor = editor;
        this.editorContent = editorContent;

        this.bindEvents();
    },

    /**
     * Cleans-up editor.
     */
    beforeDestroy() {
        this.unbindEvents();
    },

    methods: {
        /**
         * Initializes editor with its configuration.
         */
        async initializeEditor() {
            return new Promise(resolve => {
                // Switch align property from class-based to inline styles.
                Quill.register(Quill.import('attributors/style/align'), true);

                const editor = new Quill(this.container, {
                    theme: 'snow', // required, enables toolbar with default styles
                    modules: {
                        toolbar: [
                            [{ header: [1, 2, false] }],
                            [{ background: [] }, { color: [] }],
                            ['bold', 'italic', 'underline', 'strike'],
                            ['blockquote', 'code-block'],
                            [
                                { align: [false, 'center', 'right'] },
                                { list: 'bullet' },
                                { list: 'ordered' },
                            ],
                        ],
                    },
                });

                const editorContent = this.container.querySelector('.ql-editor');

                resolve({ editor, editorContent });

                // Paste HTML from v-model
                editor.clipboard.dangerouslyPasteHTML(this.value);

                // Emit editor content after initialization
                this.emitContent();
            });
        },

        /**
         * Binds editor events.
         */
        bindEvents() {
            this.editor.on('text-change', this.emitContent);
            this.container.addEventListener('scroll', throttle(this.onContainerScoll, 100));
        },

        /**
         * Unbinds editor events.
         */
        unbindEvents() {
            this.editor.off('text-change', this.emitContent);
        },

        /**
         * Emits content of editor.
         */
        emitContent() {
            /**
             * Quill uses Delta (JSON representation of content) to display HTML
             * in editor section. However content can be fetched from
             * `.ql-editor` element inside container.
             *
             * @see https://github.com/quilljs/quill/issues/903
             *
             * @type {string}
             */
            const content = this.editorContent.innerHTML;

            this.$emit('change', content);
        },

        /**
         * Scrolls editor to top.
         */
        onScrollTopClick() {
            this.container.scrollTo({ top: 0, behavior: 'smooth' });
        },

        /**
         * Handles scroll events on editor content.
         */
        onContainerScoll() {
            const containerHeight = this.container.getBoundingClientRect().height;
            const editorContentHeight = this.editorContent.getBoundingClientRect().height;
            const scrollTop = this.container.scrollTop;
            const scrollPercentage = (scrollTop / (editorContentHeight - containerHeight)) * 100;

            this.showScrollTop = 50 < scrollPercentage;
        },
    },
};
</script>
